/**
 * Robust photo uploader — designed for iOS Safari camera captures.
 *
 * Why we do NOT use axios here:
 *   axios's multipart handling has, in the past, mis-serialised FormData that
 *   contains a Blob without a filename or without a Content-Type — both very
 *   common when iPhones/iPads use `<input type=file capture=environment>`.
 *   Using native `fetch` gives the browser full control over the multipart
 *   boundary and body, which is exactly what FastAPI's `UploadFile` expects.
 *
 * Extra safety:
 *   • Always re-encode the image to JPEG via `<canvas>` first. This normalises
 *     HEIC, PNG, missing MIME types, missing filenames, and 0-byte quirks that
 *     iOS sometimes returns.
 *   • On failure, surface the actual File / Blob metadata via toast so the
 *     user can screenshot and send it to us for debugging on real devices.
 */
import { toast } from 'sonner';

const MAX_DIM = 1600; // longest edge; keep uploads WhatsApp-sized

// Read the file into an <img> so we can draw it on a canvas.
const loadImage = (blob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode image (HEIC/format not supported by browser)'));
    };
    img.src = url;
  });

// Convert any input file/blob to a JPEG Blob via canvas. Guarantees:
//   - result.type === 'image/jpeg'
//   - result.size > 0
const normaliseToJpeg = async (file) => {
  const img = await loadImage(file);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) throw new Error('Image has zero dimensions');
  const scale = Math.min(1, MAX_DIM / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff'; // flatten any alpha for JPEG
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      'image/jpeg',
      0.85,
    ),
  );
  if (!blob || blob.size === 0) throw new Error('Encoded blob is empty');
  return blob;
};

const describeFile = (f) => {
  if (!f) return 'file=null';
  return `name=${f.name || '∅'} · type=${f.type || '∅'} · size=${f.size ?? '∅'}B`;
};

/**
 * Upload a photo to `/api/upload` and return the file URL.
 * Throws on failure with a human-readable message.
 */
export async function uploadPhotoFile(file, { debug = false } = {}) {
  if (!file) throw new Error('No file selected');

  if (debug) toast.message('Uploading', { description: describeFile(file) });

  // 1. Re-encode to a clean JPEG blob. If the browser can't decode the source
  //    (rare — mostly HEIC on older browsers), fall through and try the
  //    original blob as a last resort.
  let payload;
  let filename = 'photo.jpg';
  try {
    payload = await normaliseToJpeg(file);
  } catch (e) {
    if (debug) toast.warning('Re-encode failed', { description: `${e.message} — sending original` });
    payload = file;
    filename = file.name || 'photo.jpg';
  }

  // 2. Build the multipart body. The browser will pick the correct boundary.
  const fd = new FormData();
  fd.append('file', payload, filename);

  // 3. Send via fetch. We attach the token manually because we're bypassing
  //    the axios interceptor.
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('authToken');
  const res = await fetch(`${backend}/api/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') detail = body.detail;
      else if (Array.isArray(body?.detail))
        detail = body.detail.map((e) => `${(e.loc || []).slice(-1)[0]}: ${e.msg}`).join('; ');
    } catch { /* body wasn't JSON */ }
    // Show the file metadata alongside the failure so the user can send us a
    // screenshot that pins down the actual on-device payload shape.
    throw new Error(`${detail} — ${describeFile(file)} → ${describeFile(payload)}`);
  }

  const data = await res.json();
  return data.url;
}
